import axios from "axios";
import i18n from "../../i18n";

// const API_URL = "https://backend-proxy.yros.io"
// const API_URL = "http://127.0.0.1:5003"
// const API_BASE_URL = API_URL + "/proxy/api";
const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5003";
const API_BASE_URL = API_URL + (process.env.REACT_APP_API_BASE_URL || "/proxy/api");

const defaultHeaders = {
    Accept: "application/json",
    "Content-Type": "application/json",
};

const getSessionExpiredMessage = () => i18n.t('apiService.sessionExpired');
const getNetworkErrorMessage = () => i18n.t('apiService.networkError');

// JWT now travels in HttpOnly cookies set by the proxy at /login. The proxy
// also sets a non-HttpOnly csrf_access_token cookie that we must echo back in
// the X-CSRF-TOKEN header on mutating requests (double-submit pattern).
const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

// Staging vit sous un sous-domaine de la prod (app.staging.yros.io), donc les
// cookies posés par la prod sur .yros.io lui arrivent aussi. Le proxy suffixe
// ses cookies par environnement pour lever l'ambiguïté ; on lit le même nom
// ici. Doit valoir le JWT_COOKIE_SUFFIX du proxy, vide en prod.
const COOKIE_SUFFIX = process.env.REACT_APP_COOKIE_SUFFIX || "";

function readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
    return match ? decodeURIComponent(match[1]) : null;
}

function readCsrfToken() {
    return readCookie(`csrf_access_token${COOKIE_SUFFIX}`);
}

function redirectToForbidden() {
    if (window.location.pathname !== "/403") {
        window.location.href = "/403";
    }
}

class ApiService {
    constructor() {
        this.refreshTokenPromise = null;

        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            headers: defaultHeaders,
            withCredentials: true,
        });

        this.axiosInstance.interceptors.request.use(
            (config) => {
                const method = (config.method || "get").toLowerCase();
                if (MUTATING_METHODS.has(method)) {
                    const csrf = readCsrfToken();
                    if (csrf) {
                        config.headers["X-CSRF-TOKEN"] = csrf;
                    }
                }
                return config;
            },
            (error) => { throw error; }
        );

        this.axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 403) {
                    redirectToForbidden();
                    throw this.enrichError(error);
                }

                if (error.response && error.response.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;
                    try {
                        await this.refreshToken();
                        return this.axiosInstance(originalRequest);
                    } catch (refreshError) {
                        console.error("Session expirée. Déconnexion...");
                        this.logoutUser(getSessionExpiredMessage());
                        throw this.enrichError(refreshError);
                    }
                }   

                if (error.response && error.response.status === 401) {
                    this.logoutUser(getSessionExpiredMessage());
                }

                if (!error.response && this.shouldRetryNetworkError(originalRequest)) {
                    originalRequest._networkRetry = true;
                    await this.delay(500);
                    return this.axiosInstance(originalRequest);
                }

                throw this.enrichError(error);
            }
        );
    }

    delay(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    shouldRetryNetworkError(requestConfig) {
        if (!requestConfig || requestConfig._networkRetry) {
            return false;
        }

        const method = (requestConfig.method || "get").toLowerCase();
        return ["get", "head", "options"].includes(method);
    }

    extractBackendMessage(error) {
        return (
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            null
        );
    }

    enrichError(error) {
        if (!error) {
            return error;
        }

        const backendMessage = this.extractBackendMessage(error);
        const userMessage = backendMessage || (
            error?.message === "Network Error" ? getNetworkErrorMessage() : i18n.t('apiService.genericError')
        );

        error.backendMessage = backendMessage;
        error.userMessage = userMessage;

        return error;
    }

    async refreshToken() {
        // The HttpOnly refresh_token_cookie is sent automatically thanks to
        // withCredentials. CSRF for refresh comes from csrf_refresh_token.
        if (!this.refreshTokenPromise) {
            const csrfRefresh = readCookie(`csrf_refresh_token${COOKIE_SUFFIX}`);
            this.refreshTokenPromise = axios.post(`${API_URL}/refresh`, {}, {
                withCredentials: true,
                headers: csrfRefresh ? { "X-CSRF-TOKEN": csrfRefresh } : {},
            }).finally(() => {
                this.refreshTokenPromise = null;
            });
        }

        await this.refreshTokenPromise;
    }

    logoutUser(message) {
        if (message) {
            sessionStorage.setItem("authMessage", message);
        }
        // Best-effort server-side cookie clearing; ignore failure (offline,
        // network error, expired session): we still want to redirect.
        axios.post(`${API_URL}/logout`, {}, { withCredentials: true }).catch(() => {});
        localStorage.removeItem("companyName");
        localStorage.removeItem('isConnected');
        localStorage.removeItem('user');
        window.location.href = "/login";
    }

    async authenticate(email, password, country) {
        return axios.post(API_URL + "/login", {
            login: email,
            password: password,
            country: country,
        }, {
            headers: defaultHeaders,
            withCredentials: true,
        });
    }

    async register(data) {
        return axios.post(API_URL + "/register", data, {
            headers: defaultHeaders,
            withCredentials: true,
        });
    }

    async getBlobFile(endpoint) {
        return this.axiosInstance.get(endpoint, {
            responseType: "blob",
        });
    }

    async getElement(endpoint) {
        return this.axiosInstance.get(endpoint, { headers: defaultHeaders });
    }

    async getElementById(endpoint, id) {
        return this.axiosInstance.get(`${endpoint}/${id}`, { headers: defaultHeaders });
    }

    async saveElement(endpoint, data, sendFile = false) {
        const headers = sendFile
            ? { ...defaultHeaders, "Content-Type": "multipart/form-data" }
            : defaultHeaders;

        return this.axiosInstance.post(endpoint, sendFile ? data : JSON.stringify(data), { headers });
    }

    async searchElement(endpoint, data) {
        return this.axiosInstance.post(endpoint, JSON.stringify(data), { headers: defaultHeaders });
    }

    async searchFile(endpoint, data) {
        return this.axiosInstance.post(endpoint, JSON.stringify(data), {
            headers: defaultHeaders,
            responseType: "blob",
        });
    }

    async updateElementById(endpoint, id, data, sendFile = false) {
        const headers = sendFile
            ? { ...defaultHeaders, "Content-Type": "multipart/form-data" }
            : defaultHeaders;

        return this.axiosInstance.put(`${endpoint}/${id}/`, sendFile ? data : JSON.stringify(data), { headers });
    }

    async updateElement(endpoint, data = {}, sendFile = false) {
        const headers = sendFile
            ? { ...defaultHeaders, "Content-Type": "multipart/form-data" }
            : defaultHeaders;

        return this.axiosInstance.put(endpoint, sendFile ? data : JSON.stringify(data), { headers });
    }

    async changeElementById(endpoint, id, data, sendFile = false) {
        const headers = sendFile
            ? { ...defaultHeaders, "Content-Type": "multipart/form-data" }
            : defaultHeaders;

        return this.axiosInstance.patch(`${endpoint}/${id}/`, sendFile ? data : JSON.stringify(data), { headers });
    }

    async deleteElement(endpoint, id) {
        return this.axiosInstance.delete(`${endpoint}/${id}/`, { headers: defaultHeaders });
    }

    async exportData({ model_name, fields, domain = [], ids = [], format = "csv", filename = "export" }) {
        const response = await this.searchElement('/export', {
            model_name,
            fields,
            domain,
            ids
        });
        const data = response.data.result.data || [];

       if (format === "csv") {
            const csvRows = [];
            csvRows.push(fields.join(';'));
            data.forEach(row => {
                csvRows.push(
                    fields.map(f => {
                        if ((f === "employee_id" || f === "absence_type_id") && row[f][1]) {
                            return row[f][1];
                        }
                        const val = row[f];
                        return (val === false || val === undefined || val === null) ? "" : val;
                    }).join(';')
                );
            });
            const blob = new Blob([csvRows.join('\r\n')], { type: "text/csv" });
            this.downloadBlob(blob, filename + ".csv");
        } else if (format === "xlsx") {
            const XLSX = await import("xlsx");
            const cleanData = data.map(row =>
                Object.fromEntries(
                    fields.map(f => [
                        f,
                        ((f === "employee_id" || f === "absence_type_id" )&& row.employee_id && Array.isArray(row[f]) && row[f][1])
                            ? row[f][1]
                            : ((row[f] === false || row[f] === undefined || row[f] === null) ? "" : row[f])
                    ])
                )
            );
            const ws = XLSX.utils.json_to_sheet(cleanData, { header: fields });
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Export");
            const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            this.downloadBlob(blob, filename + ".xlsx");
        } else {
            throw new Error(i18n.t('apiService.unsupportedFormat'));
        }
    }

    downloadBlob(blob, filename) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }

    async sendEmail({ to, cc, subject, html, template_id, params, attachment }) {
        // Use axios instance so Authorization header is attached by interceptor
        const response = await this.axiosInstance.post(
            `${API_URL}/api/send-email`,
            { to, cc, subject, html, template_id, params, attachment },
            { headers: { "Content-Type": "application/json" } }
        );
        return response.data;
    }

    getApiErrorMessage(error, defaultMessage) {
        if (error.response?.status === 403) {
            return i18n.t('employeeDetails.insufficientRights');
        }
        return defaultMessage;
    }
}

export const apiService = new ApiService();
